import type { CandidateWorkExperienceResponseApi } from '@/modules/candidates/types';
import { SHORTLIST_CARD_WORK_EXPERIENCE_COUNT } from '@/modules/public-shortlist/constants';
import type {
  PublicCandidateJobDetailResponseApi,
  PublicCandidateJobResponseApi,
  PublicCandidateJobSummaryResponseApi,
  PublicCandidateWorkExperienceResponseApi,
} from '@/modules/public-shortlist/types';

function mapSummary(summary: PublicCandidateJobSummaryResponseApi) {
  return { summary: summary.summary, explanation: summary.explanation };
}

function mapWorkExperience(
  experience: PublicCandidateWorkExperienceResponseApi,
) {
  return {
    jobTitle: experience.jobTitle?.trim() || 'Role confidential',
    companyName: experience.companyName?.trim() || 'Company confidential',
    startDate: experience.startDate,
    endDate: experience.endDate,
  };
}

function getCandidateFullName(firstName: string, lastName: string) {
  return `${firstName} ${lastName}`.trim() || 'Candidate';
}

export function mapPublicCandidateJob(
  candidate: PublicCandidateJobResponseApi,
) {
  const profileHighlights = candidate.profileHighlights;

  return {
    id: candidate.id,
    fullName: getCandidateFullName(candidate.firstName, candidate.lastName),
    title: candidate.jobTitle?.trim() || 'Candidate',
    company: candidate.currentCompany?.trim() || 'Company confidential',
    location: candidate.location?.trim() || null,
    linkedinUrl: candidate.linkedinUrl?.trim() || null,
    summary: profileHighlights?.summary.trim() || null,
    strengths: (profileHighlights
      ? profileHighlights.strengths
      : (candidate.strengths ?? [])
    ).map(mapSummary),
    workExperience: (candidate.workExperience ?? []).map(mapWorkExperience),
  };
}

export function mapPublicCandidateJobDetail(
  candidate: PublicCandidateJobDetailResponseApi,
) {
  return mapPublicCandidateJob(candidate);
}

export type ShortlistCandidate = ReturnType<typeof mapPublicCandidateJob>;
export type ShortlistCandidateDetail = ShortlistCandidate;

export function mapPublicWorkExperiencesForCard(
  candidate: ShortlistCandidate,
): CandidateWorkExperienceResponseApi[] {
  if (candidate.workExperience.length > 0) {
    return candidate.workExperience
      .slice(0, SHORTLIST_CARD_WORK_EXPERIENCE_COUNT)
      .map((experience, index) => ({
        id: `${candidate.id}-experience-${index}`,
        jobTitle: experience.jobTitle,
        companyName: experience.companyName,
        startDate: experience.startDate ?? undefined,
        endDate: experience.endDate ?? undefined,
      }));
  }

  return [
    {
      id: `${candidate.id}-current-role`,
      jobTitle: candidate.title,
      companyName: candidate.company,
    },
  ];
}
